// API configuration
export const API_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001"; // Base URL without /api

// API Key for backend authentication
export const API_KEY = import.meta.env.VITE_API_KEY || "development-api-key";

// File upload configuration
export const ALLOWED_FILE_TYPES = [
  ".pdf",
  ".docx",
  ".txt",
  ".md",
  ".doc",
  ".rtf",
  ".odt",
];
export const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB for chunked uploads
export const MAX_FILES_PER_UPLOAD = 10;

// Document status types
export const DOCUMENT_STATUS = {
  SUCCESS: "success",
  PROCESSING: "processing",
  ERROR: "error",
  UPLOADING: "uploading",
  PARSING: "parsing",
  INDEXING: "indexing",
} as const;

// Chat configuration
export const DEFAULT_SYSTEM_MESSAGE =
  "I am an advanced AI assistant that helps you analyze and understand your documents. I can answer questions, summarize content, and provide insights based on your uploaded knowledge base.";
export const MIN_RELEVANCE_SCORE = 0.7;
export const MAX_CHAT_HISTORY = 50;
export const CHAT_RESPONSE_TIMEOUT = 30000; // 30 seconds

// Knowledge base configuration
export const KNOWLEDGE_BASE = {
  MAX_DOCUMENTS: 1000,
  CHUNK_SIZE: 1000,
  CHUNK_OVERLAP: 200,
  SIMILARITY_THRESHOLD: 0.8,
  MAX_RETRIEVAL_RESULTS: 10,
} as const;

// API endpoints
export const ENDPOINTS = {
  // Knowledge base endpoints
  KNOWLEDGE_UPLOAD: "/api/knowledge/documents/upload",
  KNOWLEDGE_DOCUMENTS: "/api/knowledge/documents",
  KNOWLEDGE_DELETE: "/api/knowledge/documents/{id}",
  KNOWLEDGE_REPROCESS: "/api/knowledge/documents/{id}/reprocess",
  KNOWLEDGE_STATS: "/api/knowledge/stats",
  KNOWLEDGE_SEARCH: "/api/knowledge/search",
  KNOWLEDGE_SUMMARY: "/api/knowledge/summary",

  // Chat endpoints
  CHAT: "/api/knowledge/chat",
  CHAT_STREAM: "/api/knowledge/chat/stream",
  CHAT_HISTORY: "/api/knowledge/chat/sessions",

  // Legacy endpoints
  DOCUMENTS: "/api/scopingai/documents",
  PROPOSALS: "/api/scopingai/proposals",
  PROPOSALS_STREAM: "/api/scopingai/proposals/stream",
  HEALTH: "/api/scopingai/health",
  REPROCESS: "/api/scopingai/documents/{id}/reprocess",
} as const;

// Toast messages
export const TOAST_MESSAGES = {
  UPLOAD_SUCCESS: "Document uploaded successfully!",
  UPLOAD_ERROR: "Failed to upload document. Please try again.",
  UPLOAD_PROCESSING: "Processing document...",
  DELETE_SUCCESS: "Document deleted successfully!",
  DELETE_ERROR: "Failed to delete document.",
  CHAT_ERROR: "Failed to send message. Please try again.",
  CONNECTION_ERROR: "Connection error. Please check your internet connection.",
} as const;

export enum MemberRole {
  OWNER = "owner",
  ADMIN = "admin",
  MEMBER = "member",
}

export enum EntityType {
  ORGANIZATION = "organization",
  TEAM = "team",
}

export const ENTITY_LABELS = {
  [EntityType.ORGANIZATION]: {
    singular: "Organisation",
    plural: "Organisations",
  },
  [EntityType.TEAM]: {
    singular: "Team",
    plural: "Teams",
  },
};

// Admin portal configuration
export const ADMIN_CONFIG = {
  PLAN_TIERS: {
    DEFAULT_PAGE_SIZE: 10,
    MAX_PAGE_SIZE: 100,
    MIN_TIER_LEVEL: 0,
    MAX_TIER_LEVEL: 999,
    DEFAULT_FEATURES: {
      scopingai: {
        proposals_limit: 5,
        advanced_templates: false,
        priority_support: false,
        api_access: false,
      },
      knowledgebase: {
        documents_limit: 5,
      },
    },
  },
  USER_MANAGEMENT: {
    DEFAULT_PAGE_SIZE: 10,
    MAX_PAGE_SIZE: 100,
    ROLES: {
      INITIAL: "initial",
      USER: "user",
      DISAPPROVED: "disapproved",
      ADMIN: "admin",
    },
    CONTACT_INFO: {
      EMAIL: "admin@altzero.com",
      PHONE: "+1 (555) 123-4567",
    },
  },
  ROLES: {
    ADMIN: "admin",
    USER: "user",
  },
} as const;

// Branding configuration
export const BRANDING_CONFIG = {
  LOGO: {
    MAX_SIZE: 5 * 1024 * 1024, // 5MB
    ALLOWED_TYPES: ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"],
    BUCKET_NAME: "brandinglogos",
  },
  COLORS: {
    DEFAULT_PRIMARY: "#3B82F6",
    DEFAULT_SECONDARY: "#6B7280",
    DEFAULT_ACCENT: "#10B981",
    DEFAULT_FONT_COLOR: "#1F2937",
  },
  FONTS: [
    { value: "Inter", label: "Inter" },
    { value: "Roboto", label: "Roboto" },
    { value: "Open Sans", label: "Open Sans" },
    { value: "Lato", label: "Lato" },
    { value: "Montserrat", label: "Montserrat" },
    { value: "Poppins", label: "Poppins" },
    { value: "Source Sans Pro", label: "Source Sans Pro" },
    { value: "Nunito", label: "Nunito" },
  ],
  POSITIONS: ["left", "center", "right"] as const,
  THEMES: ["light", "dark", "auto"] as const,
} as const;

// Admin toast messages
export const ADMIN_TOAST_MESSAGES = {
  PLAN_CREATED: "Plan tier created successfully!",
  PLAN_UPDATED: "Plan tier updated successfully!",
  PLAN_DELETED: "Plan tier deleted successfully!",
  PLAN_CREATE_ERROR: "Failed to create plan tier. Please try again.",
  PLAN_UPDATE_ERROR: "Failed to update plan tier. Please try again.",
  PLAN_DELETE_ERROR: "Failed to delete plan tier. Please try again.",
  PLAN_LOAD_ERROR: "Failed to load plan tiers. Please refresh the page.",
  ACCESS_DENIED: "Access denied. Admin privileges required.",

  // User management messages
  USER_APPROVED: "User approved successfully!",
  USER_DISAPPROVED: "User disapproved successfully!",
  USERS_BULK_APPROVED: "Users approved successfully!",
  USER_APPROVE_ERROR: "Failed to approve user. Please try again.",
  USER_DISAPPROVE_ERROR: "Failed to disapprove user. Please try again.",
  USER_LOAD_ERROR: "Failed to load users. Please refresh the page.",

  // Branding messages
  BRANDING_UPDATED: "Branding updated successfully!",
  BRANDING_UPDATE_ERROR: "Failed to update branding. Please try again.",
  BRANDING_LOAD_ERROR: "Failed to load branding data. Please refresh the page.",
  LOGO_UPLOADED: "Logo uploaded successfully!",
  LOGO_UPLOAD_ERROR: "Failed to upload logo. Please try again.",
  LOGO_REMOVED: "Logo removed successfully!",
  LOGO_REMOVE_ERROR: "Failed to remove logo. Please try again.",
} as const;
